import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';
import _ from 'lodash';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import VariableMappingDialog from './VariableMappingDialog';
import ScheduleConfiguration from './ScheduleConfiguration';
import EventReminderConfiguration from './EventReminderConfiguration';

import './notificationWorkflow.css';

// Workflow type configurations
const WORKFLOW_TYPES = {
    ABSENCE: {
        type: 'absence',
        title: 'Absence Notification',
        subtitle: 'Daily student absence reports sent to parents',
        apiPath: 'absence',
        icon: 'pi pi-calendar-times',
        helpText: 'Notifies parents when their child is marked absent. Run Now: Select and process absences for a specific date. Schedule: When the scheduled time arrives, it processes absences for that day automatically.',
        channelMetadata: {
            email: {
                caption: 'Deliver detailed absence summaries by email'
            }
        }
    },
    FEE_PAYMENT_DUE: {
        type: 'fee-payment-due',
        title: 'Upcoming Payment Notifications',
        subtitle: 'Notify parents about pending fee payments',
        apiPath: 'fee-due-reminder',
        icon: 'pi pi-money-bill',
        helpText: 'Sends payment reminders for fee installments due in the next 7 days. Run Now: Select a start date to check fees due from that date onwards. Schedule: When the scheduled time arrives, it checks fees due from that day onwards. Excludes fully paid installments.',
        channelMetadata: {
            email: {
                caption: 'Send fee due notifications with payment details'
            }
        }
    },
    EVENT: {
        type: 'event',
        title: 'Event Reminders',
        subtitle: 'School event announcements and reminders',
        apiPath: 'event-reminder',
        icon: 'pi pi-calendar',
        helpText: 'Sends reminders for upcoming events from the academic calendar. Notifications are automatically triggered when events are created or updated. Configure multiple reminders (e.g., 7 days before, 3 days before, 1 day before). For daily recurring events, only one notification is sent for the entire series. For weekly/monthly/yearly events, separate notifications are sent for each occurrence.',
        hideRunNow: true, // Event reminders are triggered automatically, not manually
        hideSchedule: true, // Event reminders use reminder configuration, not schedule
        channelMetadata: {
            email: {
                caption: 'Send event details with date, time, and venue information'
            }
        }
    },
    EXAM_RESULT: {
        type: 'exam-result',
        title: 'Examination Results',
        subtitle: 'Notify parents when exam results are published',
        apiPath: 'exam-result',
        icon: 'pi pi-file',
        description: 'Send exam results and performance reports to parents',
        helpText: 'Notifies parents when exam results are published. Configure templates and variable mappings here. Notifications are sent from the exam list screens by clicking the "Notify Parents" button after results are published.',
        hideRunNow: true, // Don't show Run Now button for exam results
        hideSchedule: true, // Don't show Schedule configuration for exam results
        channelMetadata: {
            email: {
                caption: 'Send detailed exam reports with marks and analysis'
            }
        }
    }
};

const CHANNEL_METADATA = {
    sms: {
        title: 'SMS Notifications',
        caption: 'Send instant text messages to guardians',
        icon: 'pi pi-mobile',
        templateKey: 'sms',
        templateLabel: 'SMS Template'
    },
    email: {
        title: 'Email Notifications',
        caption: 'Deliver detailed summaries by email',
        icon: 'pi pi-envelope',
        templateKey: 'email',
        templateLabel: 'Email Template'
    },
    whatsapp: {
        title: 'WhatsApp Notifications',
        caption: 'Notify parents on WhatsApp using approved templates',
        icon: 'pi pi-whatsapp',
        templateKey: 'whatsapp',
        templateLabel: 'WhatsApp Template'
    },
    alert: {
        title: 'In-app Alerts',
        caption: 'Push alerts to mobile apps for parents or students',
        icon: 'pi pi-bell',
        templateKey: 'push',
        templateLabel: 'Push Template'
    }
};

const CONFIG_STATUS_META = {
    CONFIGURED: { severity: 'success', label: 'Configured' },
    MISSING_CONFIGURATION: { severity: 'danger', label: 'Missing config' },
    READ_ONLY: { severity: 'info', label: 'Managed externally' },
    DISABLED: { severity: 'warning', label: 'Disabled' }
};

const WORKFLOW_STATUS_OPTIONS = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Ready', value: 'READY' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Disabled', value: 'DISABLED' }
];

const DELIVERY_OPTIONS = [
    { label: 'Manual Only', value: 'MANUAL_ONLY' },
    { label: 'Daily', value: 'DAILY' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Schedule Once', value: 'SCHEDULE_ONCE' }
];

const formatCurrency = (value = 0) => {
    try {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(Number(value || 0));
    } catch (error) {
        return `₹${Number(value || 0).toFixed(2)}`;
    }
};

const getConfigMeta = (configurationStatus) => {
    return CONFIG_STATUS_META[configurationStatus] || { severity: 'secondary', label: configurationStatus || 'Unknown' };
};

const NotificationWorkflow = () => {
    // All hooks must be called before any conditional returns
    const toastRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [workflows, setWorkflows] = useState({});  // Changed to object keyed by apiPath
    const [templates, setTemplates] = useState({});
    const [walletBalance, setWalletBalance] = useState(0);
    // config cards & yellow banner are hidden per permission; keeping data internally if needed later
    const [configurationSummary, setConfigurationSummary] = useState({});
    const [alertDetails, setAlertDetails] = useState({});
    const [hasChanges, setHasChanges] = useState({});  // Changed to object keyed by apiPath
    const [error, setError] = useState(null);
    const originalWorkflowsRef = useRef({});  // Changed to object keyed by apiPath
    const serviceRef = useRef(new Service());

    // Template chooser modal
    const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
    const [templateDialogActiveChannel, setTemplateDialogActiveChannel] = useState('sms');
    const [templateDialogSelected, setTemplateDialogSelected] = useState(null);
    const [templateDialogWorkflowApiPath, setTemplateDialogWorkflowApiPath] = useState(null);  // Track which workflow
    const [templateMappingStatus, setTemplateMappingStatus] = useState({}); // Track which templates have mappings: { templateId: { mapped: true, mappings: {...} } }

    // Template preview modal
    const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    // Variable mapping modal
    const [variableMappingDialogVisible, setVariableMappingDialogVisible] = useState(false);
    const [variableMappingData, setVariableMappingData] = useState({
        template: null,
        variables: [],
        availableFields: [],
        existingMappings: {},
        channelKey: null,
        chosenTemplate: null,
    });
    const [loadingVariables, setLoadingVariables] = useState(false);

    const fetchWorkflow = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch all workflows in parallel
            const workflowTypes = Object.values(WORKFLOW_TYPES);
            const fetchPromises = workflowTypes.map(config =>
                serviceRef.current.get(`${baseUrlAdmin}/notification-workflows/${config.apiPath}`, true)
                    .then(response => ({ apiPath: config.apiPath, config, response }))
                    .catch(error => ({ apiPath: config.apiPath, config, error }))
            );

            const results = await Promise.all(fetchPromises);

            const workflowsData = {};
            const originalData = {};
            let sharedTemplates = {};
            let sharedWalletBalance = 0;
            let sharedConfigSummary = {};
            let sharedAlertDetails = {};

            console.log('[NotificationWorkflow] Fetch results:', results);

            results.forEach(({ apiPath, config, response, error }) => {
                if (error) {
                    console.warn(`[NotificationWorkflow] Failed to fetch ${config.title}:`, error);
                    // Create a default workflow structure even if API fails
                    const defaultWorkflow = {
                        name: config.title,
                        description: config.description,
                        status: 'DRAFT',
                        isEnabled: false,
                        settings: {
                            delivery: 'MANUAL_ONLY',
                            schedule: {}
                        },
                        channels: [
                            { channelKey: 'sms', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'email', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'whatsapp', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'alert', isEnabled: false, configurationStatus: 'CONFIGURED' }
                        ],
                        configurationSummary: {},
                        updatedAt: new Date().toISOString()
                    };
                    workflowsData[apiPath] = defaultWorkflow;
                    originalData[apiPath] = _.cloneDeep(defaultWorkflow);
                } else if (response?.status && response?.res?.status) {
                    const data = response.res.data || {};
                    const fetchedWorkflow = data.workflow || {};

                    workflowsData[apiPath] = fetchedWorkflow;
                    originalData[apiPath] = _.cloneDeep(fetchedWorkflow);

                    // Use templates and wallet balance from first successful response
                    if (Object.keys(sharedTemplates).length === 0) {
                        sharedTemplates = data.templates || {};
                        sharedWalletBalance = data.walletBalance || 0;
                        sharedConfigSummary = fetchedWorkflow.configurationSummary || {};
                        sharedAlertDetails = data.workflow.alertDetails || {};
                    }
                } else {
                    console.warn(`[NotificationWorkflow] Invalid response for ${config.title}:`, response);
                    // Create default workflow for invalid responses too
                    const defaultWorkflow = {
                        name: config.title,
                        description: config.description,
                        status: 'DRAFT',
                        isEnabled: false,
                        settings: {
                            delivery: 'MANUAL_ONLY',
                            schedule: {}
                        },
                        channels: [
                            { channelKey: 'sms', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'email', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'whatsapp', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'alert', isEnabled: false, configurationStatus: 'CONFIGURED' }
                        ],
                        configurationSummary: {},
                        updatedAt: new Date().toISOString()
                    };
                    workflowsData[apiPath] = defaultWorkflow;
                    originalData[apiPath] = _.cloneDeep(defaultWorkflow);
                }
            });

            console.log('[NotificationWorkflow] Final workflows data:', workflowsData);

            originalWorkflowsRef.current = originalData;
            setWorkflows(workflowsData);
            setTemplates(sharedTemplates);
            setWalletBalance(sharedWalletBalance);
            setConfigurationSummary(sharedConfigSummary);
            setAlertDetails(sharedAlertDetails);
            setHasChanges({});
        } catch (err) {
            console.error('[NotificationWorkflow] fetch error', err);
            setError(err?.message || 'Failed to load workflows');
            toastRef.current?.show({
                severity: 'error',
                summary: 'Failed to load workflows',
                detail: err?.message || 'An unexpected error occurred',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWorkflow();
    }, [fetchWorkflow]);

    // Warn user when navigating away with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            const hasAnyChanges = Object.values(hasChanges).some(changed => changed);
            if (hasAnyChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasChanges]);

    const markDirty = useCallback((apiPath) => {
        setHasChanges(prev => ({ ...prev, [apiPath]: true }));
    }, []);

    const updateWorkflow = useCallback((apiPath, updater) => {
        setWorkflows((prev) => {
            const workflow = prev[apiPath];
            if (!workflow) return prev;
            const next = typeof updater === 'function' ? updater(workflow) : { ...workflow, ...updater };
            return { ...prev, [apiPath]: next };
        });
    }, []);

    const updateChannel = useCallback((apiPath, channelKey, changes) => {
        setWorkflows((prev) => {
            const workflow = prev[apiPath];
            if (!workflow) return prev;

            const updatedChannels = (workflow.channels || []).map((channel) => {
                if (channel.channelKey !== channelKey) {
                    return channel;
                }

                const payload = typeof changes === 'function' ? changes(channel) : changes;
                return {
                    ...channel,
                    ...payload,
                    lastUpdatedAt: new Date().toISOString()
                };
            });

            return { ...prev, [apiPath]: { ...workflow, channels: updatedChannels } };
        });
        markDirty(apiPath);
    }, [markDirty]);

    const handleWorkflowToggle = (apiPath, value) => {
        updateWorkflow(apiPath, (prev) => ({ ...prev, isEnabled: value }));
        markDirty(apiPath);
    };

    const handleStatusChange = (apiPath, value) => {
        updateWorkflow(apiPath, (prev) => ({ ...prev, status: value }));
        markDirty(apiPath);
    };

    const handleDeliveryChange = (apiPath, value) => {
        updateWorkflow(apiPath, (prev) => ({
            ...prev,
            settings: {
                ...(prev.settings || {}),
                delivery: value,
                // Clear schedule when changing delivery mode or switching to MANUAL_ONLY
                schedule: value === 'DAILY' || value === 'WEEKLY' || value === 'SCHEDULE_ONCE'
                    ? (prev.settings?.schedule || {})
                    : undefined
            }
        }));
        markDirty(apiPath);
    };

    const handleScheduleChange = (apiPath, scheduleConfig) => {
        updateWorkflow(apiPath, (prev) => ({
            ...prev,
            settings: {
                ...(prev.settings || {}),
                schedule: scheduleConfig
            }
        }));
        markDirty(apiPath);
    };

    const handleRemindersChange = (apiPath, reminders) => {
        updateWorkflow(apiPath, (prev) => ({
            ...prev,
            settings: {
                ...(prev.settings || {}),
                reminders: reminders
            }
        }));
        markDirty(apiPath);
    };

    /**
     * Fetch template variables and available fields for mapping
     */
    const fetchTemplateVariables = async (templateId, templateType, channelKey, workflowApiPath) => {
        setLoadingVariables(true);
        try {
            const response = await serviceRef.current.post(
                `${baseUrlAdmin}/notification-workflows/template/variables`,
                {
                    templateId,
                    templateType,
                    workflowType: workflowApiPath // Pass workflow type to get correct fields
                },
                true
            );

            if (response?.status && response?.res?.status) {
                return response.res.data;
            } else {
                throw new Error(response?.errMessage || response?.res?.message || 'Failed to fetch template variables');
            }
        } catch (error) {
            console.error('[fetchTemplateVariables] Error:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to fetch template variables',
                life: 3000,
            });
            return null;
        } finally {
            setLoadingVariables(false);
        }
    };

    /**
     * Open variable mapping dialog for a template (called from within Select Template dialog)
     */
    const handleOpenMappingForTemplate = async (template, channelKey) => {
        // Fetch template variables
        const variableData = await fetchTemplateVariables(
            template._id,
            template.channelKey || CHANNEL_METADATA[channelKey]?.templateKey || channelKey,
            channelKey,
            templateDialogWorkflowApiPath
        );

        if (!variableData) {
            return;
        }

        console.log('[handleOpenMappingForTemplate] Variable data received:', variableData);

        if (!variableData.hasVariables || variableData.variables.length === 0) {
            // No variables in this template - mark as mapped automatically
            setTemplateMappingStatus(prev => ({
                ...prev,
                [template._id]: {
                    mapped: true,
                    mappings: {}
                }
            }));
            toastRef.current?.show({
                severity: 'info',
                summary: 'No Variables',
                detail: 'This template does not contain any variables to map',
                life: 3000,
            });
            return;
        }

        // Ensure availableFields is always an array
        const availableFields = Array.isArray(variableData.availableFields)
            ? variableData.availableFields
            : [];

        if (availableFields.length === 0) {
            console.error('[handleOpenMappingForTemplate] No available fields received from API');
            toastRef.current?.show({
                severity: 'error',
                summary: 'Configuration Error',
                detail: 'No field mappings available. Please contact support.',
                life: 5000,
            });
            return;
        }

        // Get existing mappings if this template was already mapped
        const existingMappings = templateMappingStatus[template._id]?.mappings || {};

        setVariableMappingData({
            template: variableData.template,
            variables: variableData.variables || [],
            availableFields: availableFields,
            existingMappings,
            channelKey,
            chosenTemplate: template,
        });
        setVariableMappingDialogVisible(true);
    };

    /**
     * Handle template selection - now just assigns the already-mapped template
     */
    const handleTemplateAssignWithMapping = async (apiPath, channelKey, chosenTemplate) => {
        if (!chosenTemplate) return;

        // Get the mappings from templateMappingStatus
        const mappingInfo = templateMappingStatus[chosenTemplate._id];

        if (!mappingInfo?.mapped) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Mapping Required',
                detail: 'Please map template variables before selecting this template',
                life: 3000,
            });
            return;
        }

        // Fetch template content
        const variableData = await fetchTemplateVariables(
            chosenTemplate._id,
            chosenTemplate.channelKey || CHANNEL_METADATA[channelKey]?.templateKey || channelKey,
            channelKey,
            apiPath
        );

        if (!variableData) {
            return;
        }

        // Assign template with mappings
        updateChannel(apiPath, channelKey, {
            templateId: chosenTemplate._id,
            templateName: chosenTemplate.name,
            templateContent: variableData.template.content,
            variableMappings: mappingInfo.mappings,
        });

        setTemplateDialogVisible(false);
        toastRef.current?.show({
            severity: 'success',
            summary: 'Template Assigned',
            detail: `Template "${chosenTemplate.name}" assigned successfully`,
            life: 3000,
        });
    };

    /**
     * Handle saving of variable mappings (from within Select Template dialog)
     */
    const handleSaveVariableMappings = async (mappings) => {
        const { chosenTemplate, channelKey } = variableMappingData;
        const apiPath = templateDialogWorkflowApiPath;

        // Store mappings in templateMappingStatus (for template selection flow)
        setTemplateMappingStatus(prev => ({
            ...prev,
            [chosenTemplate._id]: {
                mapped: true,
                mappings: mappings
            }
        }));

        // If this template is already assigned to a channel, update the channel immediately
        const workflow = workflows[apiPath];
        const channel = workflow?.channels?.find(ch => ch.channelKey === channelKey);
        if (channel && channel.templateId === chosenTemplate._id) {
            updateChannel(apiPath, channelKey, {
                variableMappings: mappings
            });
        }

        toastRef.current?.show({
            severity: 'success',
            summary: 'Variables Mapped',
            detail: `Template "${chosenTemplate.name}" variables mapped successfully. ${channel && channel.templateId === chosenTemplate._id ? 'Changes will be auto-saved.' : 'You can now select this template.'}`,
            life: 3000,
        });

        handleCloseMappingDialog();
    };

    /**
     * Handle editing existing template mappings
     */
    const handleEditTemplateMappings = async (apiPath, channelKey) => {
        const workflow = workflows[apiPath];
        const channel = workflow?.channels?.find(ch => ch.channelKey === channelKey);

        if (!channel || !channel.templateId) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'No Template',
                detail: 'Please select a template first',
                life: 3000,
            });
            return;
        }

        // Set the workflow API path FIRST before fetching
        setTemplateDialogWorkflowApiPath(apiPath);

        // Fetch template variables with correct workflow type
        const variableData = await fetchTemplateVariables(
            channel.templateId,
            channel.templateType,
            channelKey,
            apiPath
        );

        if (!variableData) {
            return;
        }

        if (!variableData.hasVariables) {
            toastRef.current?.show({
                severity: 'info',
                summary: 'No Variables',
                detail: 'This template does not contain any variables to map',
                life: 3000,
            });
            return;
        }

        setVariableMappingData({
            template: variableData.template,
            variables: variableData.variables,
            availableFields: variableData.availableFields,
            existingMappings: channel.variableMappings || {},
            channelKey,
            chosenTemplate: {
                _id: channel.templateId,
                name: channel.templateName,
                channelKey: channel.channelKey,
            },
        });
        setVariableMappingDialogVisible(true);
    };

    const handleTemplateAssign = (apiPath, channelKey, chosenTemplate) => {
        if (!chosenTemplate) return;
        handleTemplateAssignWithMapping(apiPath, channelKey, chosenTemplate);
    };

    /**
     * Handle removing/clearing a template from a channel
     */
    const handleRemoveTemplate = (apiPath, channelKey) => {
        updateChannel(apiPath, channelKey, {
            templateId: null,
            templateName: '',
            templateContent: null,
            variableMappings: {},
            isEnabled: false,
        });
        toastRef.current?.show({
            severity: 'info',
            summary: 'Template Removed',
            detail: 'Template has been removed from this channel',
            life: 3000,
        });
    };

    /**
     * Handle closing variable mapping dialog and reset state
     */
    const handleCloseMappingDialog = () => {
        setVariableMappingDialogVisible(false);
        // Reset mapping data to ensure clean state for next open
        setTimeout(() => {
            setVariableMappingData({
                template: null,
                variables: [],
                availableFields: [],
                existingMappings: {},
                channelKey: null,
                chosenTemplate: null,
            });
        }, 300); // Delay to allow dialog close animation
    };

    const canToggleChannel = (workflow, channel) => {
        if (!channel) return false;
        if (!workflow?.isEnabled) return false; // Disable when workflow is OFF
        if (channel.isReadOnly) return false;
        if (!channel.templateId) return false; // Require template assignment
        return channel.configurationStatus === 'CONFIGURED';
    };

    const canEditTemplate = (workflow, channel) => {
        if (!channel) return false;
        if (!workflow?.isEnabled) return false; // Disable when workflow is OFF
        return channel.configurationStatus === 'CONFIGURED';
    };

    const getEnabledChannelsCount = (workflow) => {
        if (!workflow?.channels) return 0;
        return workflow.channels.filter(ch => ch.isEnabled && ch.templateId).length;
    };

    const hasEnabledChannels = (workflow) => {
        return getEnabledChannelsCount(workflow) > 0;
    };

    const canRunWorkflow = (workflow) => {
        return hasEnabledChannels(workflow);
    };

    const canSaveScheduledWorkflow = (workflow) => {
        const delivery = workflow?.settings?.delivery;
        const isScheduled = ['DAILY', 'WEEKLY', 'SCHEDULE_ONCE'].includes(delivery);

        if (isScheduled && workflow?.isEnabled) {
            return hasEnabledChannels(workflow);
        }
        return true; // Allow save for manual-only or disabled workflows
    };

    const saveWorkflow = async (apiPath) => {
        const workflow = workflows[apiPath];
        if (!workflow) return;
        setSaving(prev => ({ ...prev, [apiPath]: true }));
        setError(null);

        // Get academic year from localStorage for scheduled workflows
        const academicYear = localStorage.getItem('userAcademicYear');

        const payload = {
            name: workflow.name,
            description: workflow.description,
            status: workflow.status,
            isEnabled: workflow.isEnabled,
            settings: workflow.settings,
            academicYear: academicYear || undefined,
            channels: (workflow.channels || []).map((channel) => ({
                channelKey: channel.channelKey,
                isEnabled: !!channel.isEnabled,
                templateId: channel.templateId || null,
                templateName: channel.templateName || '',
                templateType: channel.templateType,
                templateContent: channel.templateContent || null,
                variableMappings: channel.variableMappings || {},
                isReadOnly: !!channel.isReadOnly
            }))
        };

        console.log('[saveWorkflow] Saving workflow with payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await serviceRef.current.put(`${baseUrlAdmin}/notification-workflows/${apiPath}`, payload, true);

            if (response?.status && response?.res?.status) {
                // Show success toast
                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Changes Saved',
                    detail: 'Workflow configuration has been saved successfully',
                    life: 3000
                });
                originalWorkflowsRef.current[apiPath] = _.cloneDeep(response.res.data?.workflow || workflow);
                setWorkflows(prev => ({ ...prev, [apiPath]: response.res.data?.workflow || workflow }));
                setConfigurationSummary((response.res.data?.workflow || workflow)?.configurationSummary || configurationSummary);
                setHasChanges(prev => ({ ...prev, [apiPath]: false }));
            } else {
                throw new Error(response?.errMessage || response?.res?.message || 'Unable to save workflow');
            }
        } catch (err) {
            console.error('[NotificationWorkflow] save error', err);
            setError(err?.message || 'Failed to save workflow');
            toastRef.current?.show({
                severity: 'error',
                summary: 'Save failed',
                detail: err?.message || 'An unexpected error occurred',
                life: 5000
            });
        } finally {
            setSaving(prev => ({ ...prev, [apiPath]: false }));
        }
    };

    const resetChanges = (apiPath) => {
        const original = originalWorkflowsRef.current[apiPath];
        if (!original) return;
        setWorkflows(prev => ({ ...prev, [apiPath]: _.cloneDeep(original) }));
        setHasChanges(prev => ({ ...prev, [apiPath]: false }));
        toastRef.current?.show({
            severity: 'info',
            summary: 'Changes Discarded',
            detail: 'All unsaved changes have been reverted',
            life: 3000
        });
    };

    const onOpenTemplateDialog = (apiPath, channelKey) => {
        setTemplateDialogWorkflowApiPath(apiPath);
        setTemplateDialogActiveChannel(channelKey);
        const workflow = workflows[apiPath];

        // Initialize mapping status for ALL channels in this workflow, not just the current one
        const initialMappingStatus = {};

        (workflow?.channels || []).forEach((channel) => {
            const templateId = channel?.templateId ? String(channel.templateId) : null;
            if (templateId && channel?.variableMappings && Object.keys(channel.variableMappings).length > 0) {
                initialMappingStatus[templateId] = {
                    mapped: true,
                    mappings: channel.variableMappings
                };
            }
        });

        setTemplateMappingStatus(initialMappingStatus);

        // Set the selected template for the current channel being viewed
        const templateList = templates[channelKey] || templates[CHANNEL_METADATA[channelKey]?.templateKey] || [];
        const current = (workflow?.channels || []).find((c) => c.channelKey === channelKey);
        const currentTemplateId = current?.templateId ? String(current.templateId) : null;
        const selected = templateList.find((t) => String(t._id) === currentTemplateId) || null;
        setTemplateDialogSelected(selected);

        setTemplateDialogVisible(true);
    };

    const renderChannelChip = (workflow, apiPath, channel) => {
        const meta = CHANNEL_METADATA[channel.channelKey] || {};
        const toggleDisabled = !canToggleChannel(workflow, channel);
        const isChecked = !!channel.isEnabled;
        const hasTemplate = !!channel.templateId;
        const hasMappings = channel.variableMappings && Object.keys(channel.variableMappings).length > 0;

        return (
            <div key={channel.channelKey} className={`mode-chip ${isChecked ? 'checked' : ''} ${hasTemplate ? 'has-template' : ''}`}>
                <label htmlFor={`mode-${apiPath}-${channel.channelKey}`}>
                    {meta.title?.split(' ')[0] || channel.channelKey}
                    {hasTemplate && (
                        <span className="template-indicator" title={channel.templateName}>
                            ({channel.templateName})
                            {hasMappings && (
                                <i className="pi pi-link" style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: '#4caf50' }} title="Variables mapped" />
                            )}
                        </span>
                    )}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {hasTemplate && (
                        <>
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button-sm"
                                onClick={() => handleEditTemplateMappings(apiPath, channel.channelKey)}
                                tooltip="Edit template mappings"
                                tooltipOptions={{ position: 'top' }}
                                style={{ padding: '0.25rem' }}
                            />
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-sm p-button-danger"
                                onClick={() => handleRemoveTemplate(apiPath, channel.channelKey)}
                                tooltip="Remove template"
                                tooltipOptions={{ position: 'top' }}
                                style={{ padding: '0.25rem' }}
                            />
                        </>
                    )}
                    <Checkbox
                        inputId={`mode-${apiPath}-${channel.channelKey}`}
                        onChange={(e) => updateChannel(apiPath, channel.channelKey, { isEnabled: e.checked })}
                        checked={isChecked}
                        disabled={toggleDisabled}
                        {...(
                            !workflow?.isEnabled
                                ? { tooltip: 'Enable the workflow first', tooltipOptions: { position: 'top' } }
                                : !channel.templateId
                                    ? { tooltip: 'Assign a template first by clicking the + button', tooltipOptions: { position: 'top' } }
                                    : channel.configurationStatus !== 'CONFIGURED'
                                        ? { tooltip: `${meta.title} is not configured`, tooltipOptions: { position: 'top' } }
                                        : {}
                        )}
                    />
                </div>
            </div>
        );
    };

    // moved later near UI for consolidated meta rows

    const handleRunNow = (apiPath) => {
        // State for selected date (default to today)
        let selectedDate = new Date();

        confirmDialog({
            message: (
                <div>
                    <p>Select the date for which you want to process notifications:</p>
                    <div className="p-field" style={{ marginTop: '1rem' }}>
                        <label htmlFor="runNowDate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Process for date:
                        </label>
                        <Calendar
                            id="runNowDate"
                            value={selectedDate}
                            onChange={(e) => { selectedDate = e.value; }}
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()} // Can't select future dates
                            showIcon
                            style={{ width: '100%' }}
                        />
                        <small className="p-text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
                            You can select today or any past date to process
                        </small>
                    </div>
                </div>
            ),
            header: 'Run Notification Now',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-primary',
            acceptLabel: 'Run Now',
            rejectLabel: 'Cancel',
            accept: async () => {
                try {
                    // Get academic year from localStorage
                    const academicYear = localStorage.getItem('userAcademicYear');

                    const response = await serviceRef.current.post(
                        `${baseUrlAdmin}/notification-workflows/${apiPath}/run-now`,
                        {
                            date: selectedDate?.toISOString() || new Date().toISOString(),
                            academicYear: academicYear || undefined
                        },
                        true
                    );
                    if (response?.status && response?.res?.status) {
                        const processedDate = selectedDate ? moment(selectedDate).format('DD-MM-YYYY') : 'today';
                        toastRef.current?.show({
                            severity: 'success',
                            summary: 'Workflow Started',
                            detail: `Processing for ${processedDate}`,
                            life: 3000
                        });
                        // Refresh workflow data to show updated lastRunAt
                        fetchWorkflow();
                    } else {
                        throw new Error(response?.errMessage || response?.res?.message || 'Unable to run now');
                    }
                } catch (err) {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: 'Run Failed',
                        detail: err?.message || 'Unexpected error occurred',
                        life: 5000
                    });
                }
            },
            reject: () => {
                // User cancelled - do nothing
            }
        });
    };

    const getLastUpdatedText = (workflow) => {
        if (!workflow?.updatedAt) return 'Never';
        return moment(workflow.updatedAt).fromNow();
    };

    const getLastRunText = (workflow) => {
        // Check both locations: top-level lastRunAt and settings.lastRunAt
        const lastRunAt = workflow?.lastRunAt || workflow?.settings?.lastRunAt;
        if (!lastRunAt) return '—';
        return `${moment(lastRunAt).format('HH:mm')} | ${moment(lastRunAt).format('DD-MM-YYYY')}`;
    };

    const getNextScheduledText = (workflow) => {
        const delivery = workflow?.settings?.delivery;
        const schedule = workflow?.settings?.schedule;
        const scheduleInfo = workflow?.settings?.scheduleInfo;

        // Check if workflow has scheduled delivery mode
        if (!delivery || !['DAILY', 'WEEKLY', 'SCHEDULE_ONCE'].includes(delivery)) {
            // Show "Manual Only" for MANUAL_ONLY mode
            if (delivery === 'MANUAL_ONLY') {
                return 'Manual Only';
            }
            return '—';
        }

        // If workflow is disabled, no next run
        if (!workflow?.isEnabled) {
            return 'Disabled';
        }

        // Try to use backend-calculated next execution first
        if (scheduleInfo?.nextExecution) {
            return `${moment(scheduleInfo.nextExecution).format('HH:mm')} | ${moment(scheduleInfo.nextExecution).format('DD-MM-YYYY')}`;
        }

        // Calculate client-side if schedule is configured but not saved yet
        if (schedule && schedule.time) {
            try {
                const now = moment();
                let nextRun;

                if (delivery === 'DAILY') {
                    // Parse time
                    const timeParts = schedule.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                    if (timeParts) {
                        let hours = parseInt(timeParts[1], 10);
                        const minutes = parseInt(timeParts[2], 10);
                        const period = timeParts[3]?.toUpperCase();

                        if (period === 'PM' && hours !== 12) hours += 12;
                        if (period === 'AM' && hours === 12) hours = 0;

                        nextRun = moment().hours(hours).minutes(minutes).seconds(0).milliseconds(0);

                        // If time has passed today, move to tomorrow
                        if (nextRun.isSameOrBefore(now)) {
                            nextRun.add(1, 'day');
                        }

                        // If start date is provided and next run is before start date, use start date
                        if (schedule.startDate) {
                            const startDateTime = moment(schedule.startDate).hours(hours).minutes(minutes).seconds(0);
                            if (nextRun.isBefore(startDateTime)) {
                                nextRun = startDateTime;
                            }
                        }
                    }
                } else if (delivery === 'WEEKLY') {
                    if (schedule.dayOfWeek && schedule.time) {
                        const timeParts = schedule.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                        if (timeParts) {
                            let hours = parseInt(timeParts[1], 10);
                            const minutes = parseInt(timeParts[2], 10);
                            const period = timeParts[3]?.toUpperCase();

                            if (period === 'PM' && hours !== 12) hours += 12;
                            if (period === 'AM' && hours === 12) hours = 0;

                            const targetDay = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
                                .indexOf(schedule.dayOfWeek.toUpperCase());

                            nextRun = moment().day(targetDay).hours(hours).minutes(minutes).seconds(0).milliseconds(0);

                            // If the day/time has passed this week, move to next week
                            if (nextRun.isSameOrBefore(now)) {
                                nextRun.add(1, 'week');
                            }

                            // If start date is provided and next run is before start date
                            if (schedule.startDate) {
                                const startDateTime = moment(schedule.startDate).hours(hours).minutes(minutes).seconds(0);
                                if (nextRun.isBefore(startDateTime)) {
                                    // Find first occurrence of target day on or after start date
                                    const startDay = startDateTime.day();
                                    let daysToAdd = targetDay - startDay;
                                    if (daysToAdd < 0) daysToAdd += 7;
                                    nextRun = startDateTime.clone().add(daysToAdd, 'days');
                                }
                            }
                        }
                    }
                } else if (delivery === 'SCHEDULE_ONCE') {
                    if (schedule.date && schedule.time) {
                        const timeParts = schedule.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                        if (timeParts) {
                            let hours = parseInt(timeParts[1], 10);
                            const minutes = parseInt(timeParts[2], 10);
                            const period = timeParts[3]?.toUpperCase();

                            if (period === 'PM' && hours !== 12) hours += 12;
                            if (period === 'AM' && hours === 12) hours = 0;

                            nextRun = moment(schedule.date).hours(hours).minutes(minutes).seconds(0).milliseconds(0);
                        }
                    }
                }

                if (nextRun && nextRun.isValid()) {
                    return `${nextRun.format('HH:mm')} | ${nextRun.format('DD-MM-YYYY')}`;
                }
            } catch (error) {
                console.error('[nextScheduledText] Error calculating next run:', error);
            }
        }

        return 'Not configured';
    };

    return (
        <div className="notification-workflow-page">
            <Toast ref={toastRef} />
            <ConfirmDialog />
            <Tooltip target=".wallet-helper" />

            {loading && (
                <div className="workflow-loading">
                    <Skeleton width="100%" height="240px" className="mb-3" />
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <Skeleton width="100%" height="200px" className="mb-3" />
                        </div>
                        <div className="col-12 md:col-6">
                            <Skeleton width="100%" height="200px" className="mb-3" />
                        </div>
                    </div>
                </div>
            )}

            {!loading && Object.keys(workflows).length > 0 && (
                <>
                    {/* Warning banner for missing configurations */}
                    {configurationSummary.sms === 'MISSING_CONFIGURATION' && (
                        <div className="warning-banner">
                            <i className="pi pi-exclamation-triangle"></i>
                            <span>SMS notifications are not enabled but SMS services is not configured</span>
                        </div>
                    )}

                    {/* Configure section - shown once for all workflows */}
                    <div className="configure-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>Configure</h2>
                            <div className="wallet-pill">Wallet Balance: {formatCurrency(walletBalance)}</div>
                        </div>
                        <div className="configure-cards">
                            {Object.keys(CHANNEL_METADATA).map((channelKey) => {
                                const meta = CHANNEL_METADATA[channelKey];
                                const status = configurationSummary[channelKey] || 'MISSING_CONFIGURATION';
                                const getStatusText = (status, channelKey) => {
                                    if (channelKey === 'alert' && status === 'CONFIGURED') {
                                        if (alertDetails && Object.keys(alertDetails).length > 0 && (alertDetails.hasParent || alertDetails.hasStudent)) {
                                            const types = [];
                                            if (alertDetails.hasParent) types.push('Parent');
                                            if (alertDetails.hasStudent) types.push('Student');
                                            const typeText = types.length > 0 ? ` (${types.join(', ')})` : '';
                                            return `Push notifications configured${typeText}`;
                                        } else {
                                            return 'Push notifications configured';
                                        }
                                    }

                                    switch (status) {
                                        case 'CONFIGURED':
                                            return `Our ${meta.title} is integrated and active`;
                                        case 'MISSING_CONFIGURATION':
                                            return `Our ${meta.title.split(' ')[0]} gateway not configured`;
                                        case 'READ_ONLY':
                                            return `${meta.title} managed externally`;
                                        case 'DISABLED':
                                            return `${meta.title} service is disabled`;
                                        default:
                                            return `${meta.title} status unknown`;
                                    }
                                };

                                return (
                                    <div key={channelKey} className={`config-card ${status.toLowerCase()}`}>
                                        <div className="config-icon">
                                            <i className={meta.icon}></i>
                                        </div>
                                        <div className="config-content">
                                            <h4>{channelKey === 'alert' ? 'Push Notification' : meta.title.split(' ')[0]}</h4>
                                            <p className="config-status">{getStatusText(status, channelKey)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <h2 className="section-title">Notification Types</h2>

                    {/* Map over all workflows and render each one vertically */}
                    {Object.values(WORKFLOW_TYPES).map((workflowConfig) => {
                        const apiPath = workflowConfig.apiPath;
                        const workflow = workflows[apiPath];
                        const workflowHasChanges = hasChanges[apiPath] || false;
                        const workflowSaving = saving[apiPath] || false;

                        if (!workflow) return null;

                        return (
                            <div key={apiPath} className="workflow-notification-card">
                                <div className="workflow-header">
                                    <div className="workflow-info">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h3 className="workflow-title">{workflowConfig.title}</h3>
                                            {workflowConfig.helpText && (
                                                <>
                                                    <i
                                                        className="pi pi-info-circle workflow-help-icon"
                                                        style={{
                                                            fontSize: '1rem',
                                                            color: '#6366f1',
                                                            cursor: 'pointer'
                                                        }}
                                                        data-pr-tooltip={workflowConfig.helpText}
                                                        data-pr-position="right"
                                                    />
                                                    <Tooltip target={`.workflow-help-icon`} />
                                                </>
                                            )}
                                        </div>
                                        <InputSwitch
                                            checked={!!workflow.isEnabled}
                                            onChange={(e) => handleWorkflowToggle(apiPath, e.value)}
                                            className="workflow-toggle"
                                        />
                                    </div>
                                    <div className="workflow-actions">
                                        <Button
                                            label="Cancel"
                                            className="cancel-btn"
                                            onClick={() => resetChanges(apiPath)}
                                            disabled={!workflowHasChanges || workflowSaving}
                                            outlined
                                            style={{
                                                marginRight: '0.5rem',
                                                color: workflowHasChanges ? '#495057' : '#6c757d',
                                                borderColor: workflowHasChanges ? '#ced4da' : '#dee2e6',
                                                backgroundColor: 'transparent'
                                            }}
                                        />
                                        <Button
                                            label={workflowSaving ? "Saving..." : "Save"}
                                            icon={workflowSaving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                            className="save-btn"
                                            onClick={() => saveWorkflow(apiPath)}
                                            disabled={!workflowHasChanges || workflowSaving || !canSaveScheduledWorkflow(workflow)}
                                            style={{
                                                marginRight: '0.5rem',
                                                backgroundColor: workflowHasChanges && canSaveScheduledWorkflow(workflow) ? '#3b82f6' : '#94a3b8',
                                                borderColor: workflowHasChanges && canSaveScheduledWorkflow(workflow) ? '#3b82f6' : '#94a3b8',
                                                color: '#ffffff'
                                            }}
                                            {...(
                                                !canSaveScheduledWorkflow(workflow) && workflowHasChanges
                                                    ? { tooltip: 'Enable at least one notification channel to save scheduled workflows', tooltipOptions: { position: 'top' } }
                                                    : !workflow.isEnabled && workflowHasChanges
                                                        ? { tooltip: 'You can save changes even when workflow is disabled', tooltipOptions: { position: 'top' } }
                                                        : {}
                                            )}
                                        />
                                        {!workflowConfig.hideRunNow && (
                                            <Button
                                                label="Run Now"
                                                className="run-now-btn"
                                                onClick={() => handleRunNow(apiPath)}
                                                disabled={!canRunWorkflow(workflow)}
                                                tooltip={
                                                    !canRunWorkflow(workflow)
                                                        ? 'Configure and enable at least one notification channel'
                                                        : workflow.settings?.delivery === 'MANUAL_ONLY'
                                                            ? 'Manually trigger notifications for a selected date'
                                                            : workflow.settings?.delivery === 'SCHEDULE_ONCE'
                                                                ? 'Send notifications immediately for the scheduled date'
                                                                : 'Test or manually trigger scheduled notifications'
                                                }
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="modes-row" style={{ opacity: workflow.isEnabled ? 1 : 0.6 }}>
                                    {(workflow.channels || []).map((channel) => renderChannelChip(workflow, apiPath, channel))}
                                    <Button
                                        className="mode-plus-button"
                                        icon="pi pi-plus"
                                        rounded
                                        text
                                        severity="primary"
                                        onClick={() => onOpenTemplateDialog(apiPath, 'sms')}
                                        aria-label="Configure Templates"
                                        disabled={!workflow.isEnabled}
                                        tooltip={!workflow.isEnabled ? 'Enable the workflow to configure templates' : 'Configure Templates'}
                                    />
                                </div>

                                {/* Channel status indicator */}
                                <div style={{
                                    marginTop: '0.5rem',
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{
                                        color: hasEnabledChannels(workflow) ? '#10b981' : '#9ca3af',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                    }}>
                                        <i className={hasEnabledChannels(workflow) ? 'pi pi-check-circle' : 'pi pi-exclamation-circle'} style={{ fontSize: '0.875rem' }}></i>
                                        {getEnabledChannelsCount(workflow)} of {workflow.channels?.length || 0} channels configured
                                    </span>

                                    {/* Compact warning badge when scheduled delivery requires channels */}
                                    {workflow.isEnabled &&
                                        ['DAILY', 'WEEKLY', 'SCHEDULE_ONCE'].includes(workflow.settings?.delivery) &&
                                        !hasEnabledChannels(workflow) && (
                                            <span
                                                className="compact-warning-badge"
                                                style={{
                                                    backgroundColor: '#fee2e2',
                                                    color: '#dc2626',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    border: '1px solid #ef4444',
                                                    cursor: 'help',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                data-pr-tooltip="Scheduled workflows require at least one enabled channel. Click the + button to assign templates, map variables (if needed), then enable the channel checkbox."
                                                data-pr-position="top"
                                            >
                                                <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.7rem' }}></i>
                                                At least 1 channel required
                                            </span>
                                        )}
                                </div>

                                <Tooltip target=".compact-warning-badge" />

                                {/* Schedule section - hide for EVENT workflow and workflows with hideSchedule flag */}
                                {!workflowConfig.hideSchedule && (
                                    <div className="schedule-row" style={{ opacity: workflow.isEnabled ? 1 : 0.6 }}>
                                        <div className="label" style={{ display: 'flex', alignItems: 'center' }}>Schedule</div>
                                        <div className="control">
                                            <Dropdown
                                                value={workflow.settings?.delivery || 'MANUAL_ONLY'}
                                                options={DELIVERY_OPTIONS}
                                                onChange={(e) => handleDeliveryChange(apiPath, e.value)}
                                                className="status-dropdown"
                                                disabled={!workflow.isEnabled}
                                                {...(!workflow.isEnabled
                                                    ? { tooltip: 'Enable the workflow to change schedule settings', tooltipOptions: { position: 'top' } }
                                                    : {}
                                                )}
                                            />
                                        </div>
                                        <div className="spacer" />
                                        <div className="meta-row">
                                            <span className="meta">Last Triggered: {getLastRunText(workflow)}</span>
                                            <span className="meta">Next Scheduled: {getNextScheduledText(workflow)}</span>
                                            <span className="meta">Last updated: {getLastUpdatedText(workflow)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Schedule Configuration Component - hide for workflows with hideSchedule flag */}
                                {!workflowConfig.hideSchedule && workflowConfig.type !== 'event' && (
                                    <ScheduleConfiguration
                                        delivery={workflow.settings?.delivery}
                                        schedule={workflow.settings?.schedule}
                                        onChange={(scheduleConfig) => handleScheduleChange(apiPath, scheduleConfig)}
                                        disabled={!workflow.isEnabled}
                                        workflowType={workflowConfig.type}
                                    />
                                )}

                                {/* Event Reminder Configuration - show only for EVENT workflow */}
                                {apiPath === 'event-reminder' && (
                                    <EventReminderConfiguration
                                        reminders={workflow.settings?.reminders}
                                        onChange={(reminders) => handleRemindersChange(apiPath, reminders)}
                                        disabled={!workflow.isEnabled}
                                    />
                                )}

                                {/* Workflow disabled indicator */}
                                {!workflow.isEnabled && (
                                    <div className="workflow-disabled-banner" style={{
                                        padding: '0.75rem 1rem',
                                        backgroundColor: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        marginTop: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        color: '#6b7280'
                                    }}>
                                        <i className="pi pi-info-circle" style={{ fontSize: '1rem' }}></i>
                                        <span style={{ fontWeight: '500' }}>This workflow is currently disabled. Enable the toggle above to activate automatic notifications. </span>
                                    </div>
                                )}

                                {/* Unsaved changes indicator - only show if changes can be saved */}
                                {workflowHasChanges && canSaveScheduledWorkflow(workflow) && (
                                    <div className="unsaved-changes-banner" style={{
                                        padding: '0.75rem 1rem',
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffc107',
                                        borderRadius: '6px',
                                        marginTop: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        color: '#856404'
                                    }}>
                                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '1rem' }}></i>
                                        <span style={{ fontWeight: '500' }}>You have unsaved changes. Click "Save" to apply your changes or "Cancel" to discard them.</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {error && (
                        <div className="workflow-error-hint">
                            <i className="pi pi-info-circle mr-2" />
                            {error}
                        </div>
                    )}

                    {/* Template chooser dialog */}
                    <Dialog
                        header="Select Template"
                        visible={templateDialogVisible}
                        style={{ width: '60vw', maxWidth: 900 }}
                        onHide={() => setTemplateDialogVisible(false)}
                    >
                        <TabView activeIndex={['sms', 'whatsapp', 'email', 'alert'].indexOf(templateDialogActiveChannel)}
                            onTabChange={(e) => {
                                const newChannelKey = ['sms', 'whatsapp', 'email', 'alert'][e.index];
                                setTemplateDialogActiveChannel(newChannelKey);

                                // Update selected template when switching tabs
                                const workflow = workflows[templateDialogWorkflowApiPath];
                                const channel = (workflow?.channels || []).find((c) => c.channelKey === newChannelKey);
                                const templateList = templates[newChannelKey] || templates[CHANNEL_METADATA[newChannelKey]?.templateKey] || [];
                                const templateId = channel?.templateId ? String(channel.templateId) : null;
                                const selected = templateList.find((t) => String(t._id) === templateId) || null;
                                setTemplateDialogSelected(selected);
                            }}
                        >
                            {['sms', 'whatsapp', 'email', 'alert'].map((key) => {
                                const showTemplateId = key === 'sms' || key === 'whatsapp';
                                const getContent = (row) => {
                                    let content = '';

                                    // Get content based on template type and their schema fields
                                    if (key === 'sms') {
                                        content = row?.content || ''; // SMS uses 'content' field
                                    } else if (key === 'whatsapp') {
                                        content = row?.body || ''; // WhatsApp uses 'body' field
                                    } else if (key === 'email') {
                                        // Email: show subject + body in format like actual email
                                        const subject = row?.subject || '';
                                        const body = row?.body || '';
                                        content = subject && body ? `Subject: ${subject}\n\n${body}` : (subject || body);
                                    } else if (key === 'alert') {
                                        content = row?.description || ''; // Push uses 'description' field
                                    }

                                    if (!content) return `No content available (${key})`;
                                    return content.length > 100 ? content.slice(0, 100) + '...' : content;
                                };

                                const getTemplateId = (row, channelKey) => {
                                    if (channelKey === 'whatsapp') {
                                        return row?.templateId || 'No template ID';
                                    }
                                    return row?.template_id || row?.templateId || 'No template ID';
                                };

                                const showFullContent = (row) => {
                                    setPreviewTemplate({
                                        ...row,
                                        channelKey: key,
                                        showTemplateId
                                    });
                                    setPreviewDialogVisible(true);
                                };

                                const hasVariables = (row) => {
                                    // Check if template content has variables like {{1}}, {{2}}, etc.
                                    let content = '';
                                    if (key === 'sms') content = row?.content || '';
                                    else if (key === 'whatsapp') content = row?.body || '';
                                    else if (key === 'email') content = `${row?.subject || ''} ${row?.body || ''}`;
                                    else if (key === 'alert') content = row?.description || '';
                                    return /\{\{\d+\}\}/.test(content);
                                };

                                const isMapped = (row) => templateMappingStatus[row._id]?.mapped || false;

                                const canSelect = (row) => {
                                    // Can select if: no variables OR already mapped
                                    return !hasVariables(row) || isMapped(row);
                                };

                                return (
                                    <TabPanel header={CHANNEL_METADATA[key]?.title?.split(' ')[0] || key} key={key}>
                                        <DataTable value={(templates[key] || templates[CHANNEL_METADATA[key]?.templateKey] || [])} paginator rows={5} responsiveLayout="scroll">
                                            <Column header="Select" body={(row) => {
                                                const isSelectable = canSelect(row);
                                                const isSelected = templateDialogSelected?._id === row._id;
                                                return (
                                                    <Checkbox
                                                        onChange={() => {
                                                            if (isSelectable) {
                                                                // Toggle: if already selected, uncheck (set to null), otherwise select this row
                                                                setTemplateDialogSelected(isSelected ? null : row);
                                                            }
                                                        }}
                                                        checked={isSelected}
                                                        disabled={!isSelectable}
                                                        {...(!isSelectable
                                                            ? { tooltip: 'Please map variables first', tooltipOptions: { position: 'top' } }
                                                            : {}
                                                        )}
                                                    />
                                                );
                                            }} style={{ width: '6rem' }} />
                                            <Column field="name" header="Name" sortable />
                                            <Column header="Content" body={(row) => getContent(row)} style={{ maxWidth: '300px', wordBreak: 'break-word' }} />
                                            {showTemplateId && (
                                                <Column header="Template ID" body={(row) => getTemplateId(row, key)} style={{ width: '120px' }} />
                                            )}
                                            <Column header="Variables" className="variables-column" body={(row) => {
                                                const content = (() => {
                                                    if (!hasVariables(row)) {
                                                        return <Tag value="No Variables" severity="secondary" />;
                                                    }
                                                    if (isMapped(row)) {
                                                        return (
                                                            <Button
                                                                icon="pi pi-check-circle"
                                                                label="Mapped"
                                                                className="p-button-success p-button-sm p-button-text"
                                                                onClick={() => handleOpenMappingForTemplate(row, key)}
                                                                tooltip="Edit mappings"
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <Button
                                                            icon="pi pi-link"
                                                            label="Map"
                                                            className="p-button-warning p-button-sm"
                                                            onClick={() => handleOpenMappingForTemplate(row, key)}
                                                            tooltip="Map variables to fields"
                                                        />
                                                    );
                                                })();
                                                return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{content}</div>;
                                            }} headerStyle={{ textAlign: 'center' }} style={{ width: '140px' }} />
                                            <Column header="View" body={(row) => (
                                                <Button label="View" className="p-button-sm" text onClick={() => showFullContent(row)} />
                                            )} style={{ textAlign: 'right', width: '80px' }} />
                                        </DataTable>
                                    </TabPanel>
                                );
                            })}
                        </TabView>
                        <div className="dialog-actions">
                            <Button label="Save" onClick={() => {
                                handleTemplateAssign(templateDialogWorkflowApiPath, templateDialogActiveChannel, templateDialogSelected);
                            }} disabled={!templateDialogSelected} />
                            <Button label="Cancel" className="p-button-text" onClick={() => setTemplateDialogVisible(false)} />
                        </div>
                    </Dialog>

                    {/* Template Preview Dialog */}
                    <Dialog
                        header="Template Preview"
                        visible={previewDialogVisible}
                        style={{ width: '50vw', maxWidth: 600 }}
                        onHide={() => setPreviewDialogVisible(false)}
                        modal={true}
                    >
                        {previewTemplate && (
                            <div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Name:</strong> {previewTemplate?.name || 'Unnamed Template'}
                                </div>
                                {previewTemplate.showTemplateId && (
                                    <div style={{ marginBottom: '1rem' }}>
                                        <strong>Template ID:</strong> {
                                            previewTemplate.channelKey === 'whatsapp'
                                                ? (previewTemplate?.templateId || 'No template ID')
                                                : (previewTemplate?.template_id || previewTemplate?.templateId || 'No template ID')
                                        }
                                    </div>
                                )}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <strong>Content:</strong>
                                    <div style={{
                                        background: '#f9fafb',
                                        padding: '1rem',
                                        borderRadius: '6px',
                                        marginTop: '0.5rem',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        fontSize: '0.9em',
                                        lineHeight: '1.4',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        {(() => {
                                            const channelKey = previewTemplate.channelKey;
                                            if (channelKey === 'sms') {
                                                return previewTemplate?.content || 'No content available';
                                            } else if (channelKey === 'whatsapp') {
                                                return previewTemplate?.body || 'No content available';
                                            } else if (channelKey === 'email') {
                                                const subject = previewTemplate?.subject || '';
                                                const body = previewTemplate?.body || '';
                                                return subject && body ? `Subject: ${subject}\n\n${body}` : (subject || body || 'No content available');
                                            } else if (channelKey === 'alert') {
                                                return previewTemplate?.description || 'No content available';
                                            }
                                            return 'No content available';
                                        })()}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <Button
                                        label="Close"
                                        onClick={() => setPreviewDialogVisible(false)}
                                        className="p-button-primary"
                                    />
                                </div>
                            </div>
                        )}
                    </Dialog>

                    {/* Variable Mapping Dialog */}
                    <VariableMappingDialog
                        visible={variableMappingDialogVisible}
                        onHide={handleCloseMappingDialog}
                        template={variableMappingData.template}
                        variables={variableMappingData.variables}
                        availableFields={variableMappingData.availableFields}
                        existingMappings={variableMappingData.existingMappings}
                        onSave={handleSaveVariableMappings}
                    />
                </>
            )}

            {!loading && Object.keys(workflows).length === 0 && (
                <div className="workflow-empty-state">
                    <i className="pi pi-sync mb-3" />
                    <h4>Unable to load workflows</h4>
                    <p>Something went wrong while fetching the workflows. Please retry.</p>
                    <Button label="Retry" icon="pi pi-refresh" onClick={fetchWorkflow} />
                </div>
            )}
        </div>
    );
};

export default NotificationWorkflow;
